// From '@symbo.ls/utils', not the 'smbls' barrel — keeps the framework bundle
// out of the eager chunk (same fix as workspace/functions/extensionDesignSystem.js).
import { deepClone } from '@symbo.ls/utils'

export const goToComponentEditorRoute = (componentKey, el, state) => {
  const { pathname } = window.location
  const key = componentKey || state.key || state.value
  let href = `${pathname}/editor/${key}`

  if (pathname.includes('/editor')) {
    const parts = pathname.split('/')
    parts[parts.length - 1] = key
    href = parts.join('/')
  }

  if (pathname.includes('/preview')) {
    const parts = pathname.split('/')
    parts[parts.length - 1] = key
    href = parts.join('/')
  }

  return href
}

export const goToRemovedComponentEditorRoute = (componentKey) => {
  // Was `getLastLocationPath()` imported from '@symbo.ls/router' — that
  // package has never exported a `getLastLocationPath` (only
  // `getActiveRoute`, front-indexed, different semantics). The real
  // `getLastLocationPath(level, route)` is local
  // (./getLastLocationPath.js, level 0 = last path segment); importing a
  // sibling project file directly is against the sibling-import rule, so
  // inline the (level 0, current pathname) case this call site needs.
  const routeArray = window.location.pathname.split('/')
  const lastSegment = routeArray[routeArray.length - 1]
  const lastLocationPath = lastSegment ? `/${lastSegment}` : undefined

  if (lastLocationPath === `/${componentKey}`) {
    return window.location.pathname.split('/').slice(0, -2).join('/')
  }

  return window.location.pathname
}

export const goToComponentPreviewRoute = (el, state) => {
  const { pathname } = window.location
  let href = `${pathname}/preview/${state.key}`

  if (pathname.includes('/preview')) {
    const parts = pathname.split('/')
    parts[parts.length - 1] = state.key
    href = parts.join('/')
  }

  return href
}

export const getComponentStateSchema = (el) => {
  el.warn('redundant')
}

// TODO: check ifs calling is using await
export const getPageStateSchema = async (el) => {
  const { root } = el.parent.state
  const key = root.route.slice(5) || '/'
  const pageKey = key === '/main' || key === '/index' ? '/' : key

  const page = el.getData('pages')[pageKey]
  const schema = el.getSchema('pages')[pageKey]
  const designSystem = await el.getDefaultDesignSystem()

  if (!page) {
    if (pageKey === '/DesignSystem') {
      return {
        key: 'DesignSystem',
        title: 'Design System',
        value: deepClone(designSystem)
      }
    } else if (pageKey === '/Components') {
      return {
        key: 'Components',
        title: 'Components'
        // value: deepClone(ComponentsPage)
      }
    }
  }
  return {
    ...schema,
    value: deepClone(page)
  }
}

export async function toggleComponentLock() {
  const data = this.getCanvasScope()
  const { inContext } = data

  const { key } = this.state
  const type = this.getType(key)

  // Read the CURRENT locked state from the canonical root schema BEFORE
  // mutating any local state — otherwise a chain of two state.toggle()
  // calls (one on `this.state`, one on `inContext.state`) can race the
  // SDK's path-update for #2047 and wipe sibling props (fontSize, color
  // and friends in `value`) that the user just edited but hasn't yet
  // hoisted to the root tree. We let `sdk.updateData` own the propagation
  // and only nudge the two visual surfaces (gridstack widget + the local
  // states) afterwards with field-level patches.
  const currentLocked = !!this.getSchema(type)?.[key]?.locked
  const nextLocked = !currentLocked

  // gridstack only — does not touch the embedded iframe content or the
  // component's value tree.
  this.getCanvasFrameWindow().gs.update(inContext.node, {
    locked: nextLocked,
    noResize: nextLocked,
    noMove: nextLocked
  })

  // Persist the targeted field, then patch local UI mirrors with `update`
  // (merge) instead of `toggle` — `toggle` flips whatever the proxy
  // currently holds, which can drift from the truth source if a stray
  // reactive pass has already mutated `locked` underfoot.
  this.sdk.updateData([['update', ['schema', type, key, 'locked'], nextLocked]], {
    message: `"${key}" ${type.slice(0, -1)} is ${nextLocked ? 'locked' : 'unlocked'}`
  })

  this.state.update({ locked: nextLocked })
  inContext.state.update({ locked: nextLocked })
}

export async function toggleComponentFavorite() {
  const data = this.getCanvasScope()
  const { inContext } = data

  const { key } = this.state
  const type = this.getType(key)

  // See toggleComponentLock for why we read truth from getSchema rather
  // than from the local proxies, and why we patch with `update` rather
  // than `toggle` — see #2047.
  const currentHighlighted = !!this.getSchema(type)?.[key]?.highlighted
  const nextHighlighted = !currentHighlighted

  this.sdk.updateData([['update', ['schema', type, key, 'highlighted'], nextHighlighted]], {
    message: nextHighlighted
      ? `"${key}" ${type.slice(0, -1)} marked as favorite`
      : `"${key}" ${type.slice(0, -1)} removed from favorites`
  })

  this.state.update({ highlighted: nextHighlighted })
  inContext.state.update({ highlighted: nextHighlighted })
}

export async function setError() {
  const data = this.getCanvasScope()
  const { inContext } = data
  const { value, ...schema } = this.state.parse()
  const { key } = schema
  const type = this.getType(key)

  await this.state.toggle('highlighted')
  inContext.state.toggle('highlighted')
  const { highlighted } = this.getSchema(type)[key]

  this.sdk.updateData([['update', ['schema', type, key, 'highlighted'], !highlighted]], {
    message: !highlighted
      ? `"${key}" ${type.slice(0, -1)} marked as favorite`
      : `"${key}" ${type.slice(0, -1)} removed from favorites`
  })

  // await this.call('rerenderFileSidebarByType', type).update(
  //   {},
  // )
}

export async function addItemWithDependencies(component, dependencyData, opts = {}) {
  // If component exists on canvas don't add it
  const library = await this.getDefaultComponents()
  const builtInComponents = this.context.components
  const { type, key } = component
  const exists = this.getDataItem(key)
  if (exists) {
    this.return
  }

  // Duplicate the current component
  const res = await this.call('createItem', type, key, component, opts)
  if (!res.success) {
    return this.call('openNotification', {
      type: 'error',
      title: this.call('polyglot', 'editor.component.duplicateFailed.title'),
      message: this.call('polyglot', 'editor.code.evalError.message')
    })
  }

  const newComponent = this.getItem(key, type)
  if (!newComponent || !newComponent.uses || !newComponent.uses.length) {
    return
  }
  // Process all dependencies recursively
  const processDependency = async (useKey) => {
    const isBuiltIn = builtInComponents[`smbls.${useKey}`]
    if (isBuiltIn) return

    const dependency = dependencyData.find((c) => c.key === useKey)
    if (!dependency) return

    if (!dependency.value || typeof dependency.value === 'undefined') {
      dependency.value = library[useKey]
    }

    await this.call('addItemWithDependencies', dependency, dependencyData, {
      skipSelection: true
    })

    // Process nested dependencies
    if (dependency.uses?.length) {
      for (const depUseKey of dependency.uses) {
        await processDependency(depUseKey)
      }
    }
  }

  for (const useKey of newComponent.uses) {
    await processDependency(useKey)
  }
}
